<template>
  <v-container fluid class="pl-4 pr-4 pb-4 pt-1">
    <v-row align="center" class="mb-0 px-0" style="min-height: 48px;">
      <v-col cols="auto">
        <v-select
          v-model="filterPlatform"
          :items="platforms"
          item-title="display_name"
          item-value="id"
          placeholder="Все платформы"
          variant="outlined"
          density="compact"
          hide-details
          clearable
          style="min-width: 180px"
          @update:model-value="loadAccounts"
        ></v-select>
      </v-col>
      <v-col class="text-center">
        <h4 class="mb-0">Аккаунты для скрапинга</h4>
      </v-col>
      <v-col cols="auto">
        <v-btn
          color="primary"
          variant="elevated"
          @click="openCreateDialog"
          prepend-icon="mdi-plus"
        >
          Добавить аккаунт
        </v-btn>
      </v-col>
    </v-row>

    <v-card>
      <v-card-text class="pa-0">
        <v-data-table
          :headers="headers"
          :items="accounts"
          :items-per-page="-1"
          :loading="isLoading"
          class="elevation-1"
          hover
          fixed-header
          height="calc(100vh - 236px)"
          hide-default-footer
        >
          <template v-slot:[`item.platform_name`]="{ item }">
            <v-chip size="small" variant="tonal">{{ item.platform_name }}</v-chip>
          </template>

          <template v-slot:[`item.proxy_id`]="{ item }">
            <template v-if="proxyById(item.proxy_id)">
              <span class="proxy-addr">#{{ item.proxy_id }} {{ proxyById(item.proxy_id).ip_port }}</span>
              <v-chip
                v-if="proxyById(item.proxy_id).country_code"
                size="x-small"
                variant="tonal"
                class="ml-1"
              >{{ proxyById(item.proxy_id).country_code.toUpperCase() }}</v-chip>
            </template>
            <span v-else-if="item.proxy_id" class="text-warning">#{{ item.proxy_id }}</span>
            <span v-else class="text-medium-emphasis">Напрямую</span>
          </template>

          <template v-slot:[`item.auth_info`]="{ item }">
            <v-icon v-if="item.has_password" size="x-small" color="success" title="Пароль">mdi-key</v-icon>
            <v-icon v-if="item.has_auth_token" size="x-small" color="success" title="Токен" class="ml-1">mdi-shield-key</v-icon>
            <v-icon v-if="item.has_cookies" size="x-small" color="success" title="Cookies" class="ml-1">mdi-cookie</v-icon>
            <v-icon v-if="item.has_email_password" size="x-small" color="success" title="Email пароль" class="ml-1">mdi-email-check</v-icon>
          </template>

          <template v-slot:[`item.status`]="{ item }">
            <v-chip :color="statusColor(item.status)" size="small" variant="tonal">
              {{ statusLabel(item.status) }}
            </v-chip>
            <v-icon v-if="item.is_locked" size="small" color="warning" class="ml-1">mdi-lock</v-icon>
          </template>

          <template v-slot:[`item.error_count`]="{ item }">
            <span :class="item.error_count > 0 ? 'text-error' : ''">{{ item.error_count }}</span>
            <v-tooltip v-if="item.last_error" :text="item.last_error" location="top">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" size="small" color="error" class="ml-1">mdi-alert-circle-outline</v-icon>
              </template>
            </v-tooltip>
          </template>

          <template v-slot:[`item.last_used_at`]="{ item }">
            {{ formatDateTime(item.last_used_at) }}
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <v-tooltip text="Редактировать" location="top">
              <template v-slot:activator="{ props }">
                <v-btn v-bind="props" icon="mdi-pencil" size="small" variant="text" @click="openEditDialog(item)"></v-btn>
              </template>
            </v-tooltip>
            <v-tooltip v-if="item.status === 'suspended' || item.error_count > 0" text="Сбросить ошибки" location="top">
              <template v-slot:activator="{ props }">
                <v-btn v-bind="props" icon="mdi-refresh" size="small" variant="text" color="success" @click="resetErrors(item)"></v-btn>
              </template>
            </v-tooltip>
            <v-tooltip text="Удалить" location="top">
              <template v-slot:activator="{ props }">
                <v-btn v-bind="props" icon="mdi-delete" size="small" variant="text" color="error" @click="confirmDelete(item)"></v-btn>
              </template>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Create/Edit Dialog -->
    <v-dialog v-model="showDialog" max-width="600" @keydown.esc="showDialog = false">
      <v-card>
        <v-card-title>{{ editingAccount ? 'Редактирование аккаунта' : 'Новый аккаунт' }}</v-card-title>
        <v-card-text>
          <v-alert v-if="dialogError" type="error" variant="tonal" density="compact" class="mb-3" closable @click:close="dialogError = ''">
            {{ dialogError }}
          </v-alert>
          <v-select
            v-model="form.platform_id"
            :items="platforms"
            item-title="display_name"
            item-value="id"
            label="Платформа"
            variant="outlined"
            density="compact"
            :disabled="!!editingAccount"
            @update:model-value="loadProxyOptions"
          ></v-select>
          <v-text-field
            v-model="form.login"
            label="Логин (username)"
            variant="outlined"
            density="compact"
          ></v-text-field>
          <v-text-field
            v-model="form.password"
            label="Пароль"
            variant="outlined"
            density="compact"
            :type="showSecrets.password ? 'text' : 'password'"
            :append-inner-icon="showSecrets.password ? 'mdi-eye-off' : 'mdi-eye'"
            @click:append-inner="showSecrets.password = !showSecrets.password"
          >
            <template #append>
              <v-btn
                icon="mdi-content-copy"
                size="x-small"
                variant="text"
                :disabled="!form.password"
                @click="copyToClipboard(form.password, 'Пароль скопирован')"
              ></v-btn>
            </template>
          </v-text-field>
          <v-text-field
            v-model="form.display_name"
            label="Отображаемое имя"
            variant="outlined"
            density="compact"
          ></v-text-field>
          <v-text-field
            v-model="form.country"
            label="Страна (код, напр. RU)"
            variant="outlined"
            density="compact"
            maxlength="10"
          ></v-text-field>
          <v-select
            v-model="form.proxy_id"
            :items="proxyItems"
            item-title="title"
            item-value="value"
            label="Прокси"
            variant="outlined"
            density="compact"
            clearable
            :loading="proxyOptionsLoading"
            hint="Свободные для этой платформы рабочие прокси датацентра нужного ей типа"
            persistent-hint
          >
            <template v-slot:item="{ props, item }">
              <v-list-item v-bind="props" :subtitle="item.raw.subtitle"></v-list-item>
            </template>
          </v-select>
          <v-text-field
            v-model="form.email"
            label="Email"
            variant="outlined"
            density="compact"
          ></v-text-field>
          <v-text-field
            v-model="form.email_password"
            label="Пароль к email"
            variant="outlined"
            density="compact"
            :type="showSecrets.email_password ? 'text' : 'password'"
            :append-inner-icon="showSecrets.email_password ? 'mdi-eye-off' : 'mdi-eye'"
            @click:append-inner="showSecrets.email_password = !showSecrets.email_password"
          >
            <template #append>
              <v-btn
                icon="mdi-content-copy"
                size="x-small"
                variant="text"
                :disabled="!form.email_password"
                @click="copyToClipboard(form.email_password, 'Пароль к email скопирован')"
              ></v-btn>
            </template>
          </v-text-field>
          <v-text-field
            v-model="form.auth_token"
            label="Токен (auth_token)"
            variant="outlined"
            density="compact"
            :type="showSecrets.auth_token ? 'text' : 'password'"
            :append-inner-icon="showSecrets.auth_token ? 'mdi-eye-off' : 'mdi-eye'"
            @click:append-inner="showSecrets.auth_token = !showSecrets.auth_token"
          >
            <template #append>
              <v-btn
                icon="mdi-content-copy"
                size="x-small"
                variant="text"
                :disabled="!form.auth_token"
                @click="copyToClipboard(form.auth_token, 'auth_token скопирован')"
              ></v-btn>
            </template>
          </v-text-field>
          <v-select
            v-if="editingAccount"
            v-model="form.status"
            :items="statusOptions"
            item-title="text"
            item-value="value"
            label="Статус"
            variant="outlined"
            density="compact"
          ></v-select>
          <v-textarea
            v-model="cookiesText"
            label="Cookies (JSON)"
            variant="outlined"
            density="compact"
            rows="6"
            placeholder='{"ct0": "...", "auth_token": "..."}'
            hint='Объект {"имя": "значение"} или массив из экспорта браузера [{"name": ..., "value": ...}]'
            persistent-hint
          >
            <template #append>
              <v-btn
                icon="mdi-content-copy"
                size="x-small"
                variant="text"
                :disabled="!cookiesText || cookiesText === '{}'"
                @click="copyToClipboard(cookiesText, 'Cookies скопированы')"
              ></v-btn>
            </template>
          </v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showDialog = false">Отмена</v-btn>
          <v-btn color="primary" variant="elevated" @click="saveAccount">
            {{ editingAccount ? 'Сохранить' : 'Создать' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation -->
    <v-dialog v-model="showDeleteDialog" max-width="400">
      <v-card>
        <v-card-title>Удаление аккаунта</v-card-title>
        <v-card-text>Удалить аккаунт <strong>{{ deleteTarget?.login }}</strong>?</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showDeleteDialog = false">Отмена</v-btn>
          <v-btn color="error" variant="elevated" @click="doDelete">Удалить</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">
      {{ snackbar.text }}
    </v-snackbar>
  </v-container>
</template>

<script>
import apiClient from '@/apiClient';

export default {
  name: 'SocialAccounts',
  data() {
    return {
      accounts: [],
      platforms: [],
      proxies: [],
      proxyOptions: [],
      proxyOptionsLoading: false,
      isLoading: false,
      filterPlatform: null,
      headers: [
        { title: 'Платформа', key: 'platform_name', width: '120px' },
        { title: 'Логин', key: 'login' },
        { title: 'Имя', key: 'display_name' },
        { title: 'Email', key: 'email' },
        { title: 'Страна', key: 'country', width: '80px' },
        { title: 'Прокси', key: 'proxy_id', sortable: false, width: '200px' },
        { title: 'Данные', key: 'auth_info', sortable: false, width: '120px' },
        { title: 'Статус', key: 'status', width: '140px' },
        { title: 'Ошибки', key: 'error_count', width: '100px' },
        { title: 'Последнее использование', key: 'last_used_at', width: '180px' },
        { title: 'Действия', key: 'actions', sortable: false, width: '140px' },
      ],
      // Dialog
      showDialog: false,
      editingAccount: null,
      dialogError: '',
      form: { platform_id: null, login: '', password: '', display_name: '', country: '', email: '', email_password: '', auth_token: '', proxy_id: null, status: 'active' },
      cookiesText: '{}',
      showSecrets: { password: false, email_password: false, auth_token: false },
      statusOptions: [
        { text: 'Активен', value: 'active' },
        { text: 'Приостановлен', value: 'suspended' },
        { text: 'Заблокирован', value: 'banned' },
        { text: 'Cooldown', value: 'cooldown' },
      ],
      // Delete
      showDeleteDialog: false,
      deleteTarget: null,
      // Snackbar
      snackbar: { show: false, text: '', color: 'success' },
    };
  },
  async mounted() {
    await this.loadPlatforms();
    await this.loadProxies();
    await this.loadAccounts();
  },
  computed: {
    proxyItems() {
      const items = this.proxyOptions.map((p) => ({
        value: p.id,
        title: this.proxyLabel(p),
        subtitle: `${p.kind || ''} ${p.status || ''}`.trim(),
      }));
      return [{ value: null, title: 'Без прокси (напрямую)', subtitle: '' }, ...items];
    },
  },
  methods: {
    async loadPlatforms() {
      try {
        const resp = await apiClient.get('/reference/platforms');
        this.platforms = resp.data.items || [];
      } catch {
        this.platforms = [];
      }
    },
    async loadProxies() {
      try {
        const resp = await apiClient.get('/admin/social-accounts/proxies');
        this.proxies = resp.data.items || [];
      } catch {
        this.proxies = [];
      }
    },
    async loadProxyOptions() {
      this.proxyOptions = [];
      if (!this.form.platform_id) return;
      this.proxyOptionsLoading = true;
      try {
        const params = { platform_id: this.form.platform_id };
        if (this.editingAccount) params.account_id = this.editingAccount.id;
        const resp = await apiClient.get('/admin/social-accounts/proxy-options', { params });
        this.proxyOptions = resp.data.items || [];
      } catch {
        this.proxyOptions = [];
      } finally {
        this.proxyOptionsLoading = false;
      }
    },
    proxyById(proxyId) {
      if (!proxyId) return null;
      return this.proxies.find((p) => p.id === proxyId) || null;
    },
    proxyLabel(proxy) {
      const parts = [`#${proxy.id}`, proxy.ip_port];
      if (proxy.country_code) parts.push(proxy.country_code.toUpperCase());
      if (proxy.proxy_type) parts.push(proxy.proxy_type.toUpperCase());
      return parts.join(' · ');
    },
    async loadAccounts() {
      this.isLoading = true;
      try {
        const params = {};
        if (this.filterPlatform) params.platform_id = this.filterPlatform;
        const resp = await apiClient.get('/admin/social-accounts', { params });
        this.accounts = resp.data.items || [];
      } catch (e) {
        console.error('Failed to load accounts:', e);
      } finally {
        this.isLoading = false;
      }
    },
    openCreateDialog() {
      this.editingAccount = null;
      this.form = { platform_id: null, login: '', password: '', display_name: '', country: '', email: '', email_password: '', auth_token: '', proxy_id: null, status: 'active' };
      this.cookiesText = '{}';
      this.showSecrets = { password: false, email_password: false, auth_token: false };
      this.dialogError = '';
      this.proxyOptions = [];
      this.showDialog = true;
    },
    openEditDialog(account) {
      this.editingAccount = account;
      this.form = {
        platform_id: account.platform_id,
        login: account.login,
        password: account.password || '',
        display_name: account.display_name || '',
        country: account.country || '',
        email: account.email || '',
        email_password: account.email_password || '',
        auth_token: account.auth_token || '',
        proxy_id: account.proxy_id || null,
        status: account.status,
      };
      this.cookiesText = JSON.stringify(account.cookies || {}, null, 2);
      this.showSecrets = { password: false, email_password: false, auth_token: false };
      this.dialogError = '';
      this.showDialog = true;
      this.loadProxyOptions();
    },
    async copyToClipboard(text, successMessage = 'Скопировано') {
      if (!text) return;
      try {
        await navigator.clipboard.writeText(text);
        this.showSnackbar(successMessage);
      } catch {
        this.showSnackbar('Не удалось скопировать', 'error');
      }
    },
    async saveAccount() {
      if (!this.form.login) {
        this.dialogError = 'Укажите логин';
        return;
      }
      if (!this.form.platform_id) {
        this.dialogError = 'Выберите платформу';
        return;
      }
      let cookies = {};
      try {
        cookies = JSON.parse(this.cookiesText);
      } catch {
        this.dialogError = 'Некорректный JSON для cookies';
        return;
      }

      try {
        const payload = { ...this.form, cookies };
        if (this.editingAccount) {
          await apiClient.put(`/admin/social-accounts/${this.editingAccount.id}`, payload);
          this.showSnackbar('Аккаунт обновлён');
        } else {
          await apiClient.post('/admin/social-accounts', payload);
          this.showSnackbar('Аккаунт создан');
        }
        this.showDialog = false;
        await this.loadAccounts();
      } catch (e) {
        const detail = e.response?.data?.detail;
        this.dialogError = Array.isArray(detail)
          ? detail.map((d) => (d.msg || '').replace(/^Value error, /, '')).join('; ')
          : (detail || 'Ошибка сохранения');
      }
    },
    async resetErrors(account) {
      try {
        await apiClient.post(`/admin/social-accounts/${account.id}/reset-errors`);
        this.showSnackbar('Ошибки сброшены');
        await this.loadAccounts();
      } catch (e) {
        this.showSnackbar('Ошибка', 'error');
      }
    },
    confirmDelete(account) {
      this.deleteTarget = account;
      this.showDeleteDialog = true;
    },
    async doDelete() {
      try {
        await apiClient.delete(`/admin/social-accounts/${this.deleteTarget.id}`);
        this.showDeleteDialog = false;
        this.showSnackbar('Аккаунт удалён');
        await this.loadAccounts();
      } catch {
        this.showSnackbar('Ошибка удаления', 'error');
      }
    },
    statusColor(status) {
      return { active: 'success', suspended: 'warning', banned: 'error', cooldown: 'info' }[status] || 'grey';
    },
    statusLabel(status) {
      return { active: 'Активен', suspended: 'Приостановлен', banned: 'Заблокирован', cooldown: 'Cooldown' }[status] || status;
    },
    formatDateTime(dateStr) {
      if (!dateStr) return '—';
      try {
        const d = new Date(dateStr);
        return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
          ' ' + d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
      } catch { return dateStr; }
    },
    showSnackbar(text, color = 'success') {
      this.snackbar = { show: true, text, color };
    },
  },
};
</script>

<style scoped>
.proxy-addr {
  font-family: 'Roboto Mono', monospace;
  font-size: 0.8rem;
}
</style>
